import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { publicApiError } from "@/lib/apiErrors";

export default function Result() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // E-Mail-Versand-State
  const [emailInput, setEmailInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [loadMessage, setLoadMessage] = useState("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data: d } = await api.get(`/assessments/${id}`);
        if (mounted) {
          setData(d);
          // Falls die Auswertung bereits beim Fragebogen-Abschluss mit
          // E-Mail versendet wurde, gleich in den Success-State wechseln.
          if (d?.email && d?.email_sent) {
            setSent(true);
            setEmailInput(d.email);
          }
        }
      } catch (e) {
        const problem = publicApiError(e);
        setLoadMessage(problem.kind === "clarification" ? problem.message : "Wir konnten diese Auswertung nicht laden.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id]);

  const requestReport = async (e) => {
    e?.preventDefault?.();
    if (!emailInput) return;
    if (!privacyAccepted) {
      toast.error("Bitte bestätigen Sie die Datenschutzerklärung, um die Auswertung anzufordern.");
      return;
    }
    setSubmitting(true);
    try {
      const { data: r } = await api.post(
        `/assessments/${id}/contact?email=${encodeURIComponent(emailInput)}`
      );
      if (r?.email_sent) {
        toast.success("Ihre individuelle Auswertung wurde erfolgreich versendet.");
        setSent(true);
      } else {
        toast.error(
          "Versand aktuell nicht möglich. Bitte versuchen Sie es in wenigen Minuten erneut."
        );
      }
    } catch (err) {
      toast.error(publicApiError(err).message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div
        className="kk-container py-32 flex flex-col items-center text-[var(--kk-anthracite)]/60"
        data-testid="result-loading"
      >
        <Loader2 className="w-6 h-6 animate-spin mb-4" />
        <p className="text-sm">Ihre individuelle Auswertung wird vorbereitet …</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="kk-container py-32 text-center" data-testid="result-error">
        <p className="text-[var(--kk-anthracite)]/70">
          {loadMessage || "Wir konnten diese Auswertung nicht finden."}
        </p>
        <Link to="/kompass" className="kk-btn-primary mt-6 inline-flex">
          Neue Standortbestimmung
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-white" data-testid="result-page">
      <section className="border-b border-[hsl(var(--border))]">
        <div className="kk-container py-20 lg:py-28 grid lg:grid-cols-12 gap-12">
          {/* Linke Spalte: Vorschau + Schwerpunkte */}
          <div className="lg:col-span-7">
            <div className="kk-overline mb-5">Individuelle Auswertung</div>
            <h1
              className="text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] font-bold tracking-tight text-[var(--kk-anthracite)]"
              data-testid="result-headline"
            >
              Ihre individuelle Auswertung ist erstellt.
            </h1>

            <div className="mt-8 space-y-5 text-base sm:text-lg text-[var(--kk-anthracite)]/80 leading-relaxed max-w-2xl">
              <p>Vielen Dank für Ihre Teilnahme.</p>
              <p data-testid="result-management-summary">
                {data.report.management_summary}
              </p>
            </div>

            <div
              className="mt-12 pt-8 border-t border-[hsl(var(--border))] max-w-2xl text-[var(--kk-anthracite)]/85 text-base leading-relaxed"
              data-testid="full-report-hint"
            >
              Die vollständige individuelle Auswertung mit konkreten
              Handlungsempfehlungen stellen wir Ihnen gerne als PDF zur Verfügung.
            </div>
          </div>

          {/* Rechte Spalte: E-Mail-Anforderung */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              {sent ? (
                <div
                  className="bg-[var(--kk-anthracite)] text-white p-8 lg:p-10"
                  data-testid="result-success-block"
                >
                  <CheckCircle2
                    className="w-8 h-8 text-white mb-5"
                    strokeWidth={1.5}
                  />
                  <div className="text-xs uppercase tracking-[0.22em] text-white/55 mb-3">
                    Versandt
                  </div>
                  <h2
                    className="text-2xl sm:text-3xl font-bold tracking-tight leading-snug"
                    style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
                  >
                    Ihre individuelle Auswertung wurde erfolgreich versendet.
                  </h2>
                  <p
                    className="mt-5 text-sm text-white/75 leading-relaxed"
                    data-testid="result-success-text"
                  >
                    Wir haben Ihre persönliche Auswertung als PDF an{" "}
                    <span className="text-white font-medium">
                      {data.email || emailInput}
                    </span>{" "}
                    gesendet. Bitte prüfen Sie auch Ihren Spam-Ordner, falls die
                    Mail nicht in den nächsten Minuten ankommt.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={requestReport}
                  className="bg-[var(--kk-offwhite)] p-8 lg:p-10 border border-[hsl(var(--border))]"
                  data-testid="result-request-form"
                >
                  <div className="kk-overline mb-3">Auswertung erhalten</div>
                  <h2
                    className="text-2xl sm:text-3xl font-bold tracking-tight text-[var(--kk-anthracite)] leading-snug"
                    style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
                  >
                    Wohin dürfen wir Ihre Auswertung schicken?
                  </h2>

                  <label className="block mt-7">
                    <span className="text-xs uppercase tracking-[0.18em] text-[var(--kk-anthracite)]/60 font-semibold">
                      E-Mail-Adresse
                    </span>
                    <input
                      type="email"
                      required
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      placeholder="name@beispiel.de"
                      data-testid="result-email-input"
                      className="mt-2 w-full bg-transparent border-b border-[hsl(var(--border))] focus:border-[var(--kk-bluegreen)] outline-none py-2 text-[var(--kk-anthracite)] placeholder-[var(--kk-anthracite)]/30 transition-colors"
                    />
                  </label>

                  <label
                    className="flex items-start gap-3 mt-7 cursor-pointer select-none"
                    data-testid="result-privacy-label"
                  >
                    <input
                      type="checkbox"
                      checked={privacyAccepted}
                      onChange={(e) => setPrivacyAccepted(e.target.checked)}
                      data-testid="result-privacy-checkbox"
                      className="mt-1 w-4 h-4 accent-[var(--kk-bluegreen)] shrink-0"
                      required
                    />
                    <span className="text-sm text-[var(--kk-anthracite)]/85 leading-relaxed">
                      Ich habe die{" "}
                      <a
                        href="https://www.one-a-com.net/datenschutz"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline decoration-[var(--kk-bluegreen)]/50 underline-offset-4 hover:text-[var(--kk-anthracite)] hover:decoration-[var(--kk-bluegreen)]"
                        data-testid="result-privacy-link"
                      >
                        Datenschutzerklärung
                      </a>{" "}
                      gelesen und stimme der Verarbeitung meiner Daten zur
                      Erstellung und Zusendung meiner individuellen Auswertung zu.
                    </span>
                  </label>

                  <p
                    className="mt-3 text-xs text-[var(--kk-anthracite)]/55 leading-relaxed"
                    data-testid="result-privacy-hint"
                  >
                    Ihre Angaben werden zur Erstellung Ihrer individuellen Auswertung,
                    zur Bearbeitung Ihrer Anfrage sowie zur Dokumentation der
                    Ergebnisse verarbeitet. Weitere Informationen finden Sie in
                    unserer{" "}
                    <a
                      href="https://www.one-a-com.net/datenschutz"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline decoration-[var(--kk-bluegreen)]/40 underline-offset-4 hover:text-[var(--kk-anthracite)]"
                    >
                      Datenschutzerklärung
                    </a>
                    .
                  </p>

                  <button
                    type="submit"
                    disabled={submitting || !privacyAccepted || !emailInput}
                    className="kk-btn-primary mt-8 w-full sm:w-auto disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="result-submit-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Wird gesendet …
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4" /> Auswertung zusenden
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Restart-Hinweis */}
      <section className="border-t border-[hsl(var(--border))] bg-white kk-no-print">
        <div className="kk-container py-10 flex flex-wrap justify-between items-center gap-4">
          <p className="text-sm text-[var(--kk-anthracite)]/60">
            Möchten Sie eine zweite Person die Standortbestimmung durchlaufen lassen?
          </p>
          <Link to="/kompass" className="kk-btn-ghost" data-testid="restart-btn">
            Neue Standortbestimmung
          </Link>
        </div>
      </section>
    </div>
  );
}
